import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';

// The API ends each general guidance block with a link to this anchor. The chat shows it
// like a citation that explains itself on hover; it is not a link to anywhere.
export const NOT_VERIFIED_ANCHOR = '#not-verified-by-gps';
export const NOT_VERIFIED_CLASS = 'not-verified';
export const NOT_VERIFIED_LABEL = 'Not GPS-verified';
export const NOT_VERIFIED_EXPLANATION = 'This section provides additional context that may be useful, ' +
    "but has not been reviewed by GPS's legal researchers";

export interface AnswerSource {
    label: string;
    url: string;
}

/** Turns an answer from the AI chat, which is markdown, into what is shown and what is copied. */
@Injectable({
    providedIn: 'root',
})
export class ChatMarkdownService {

    constructor(private sanitizer: DomSanitizer) { }

    /** The answer as HTML that is safe to show. */
    render(markdown: string): SafeHtml {
        // Keep the "Not verified" mark on the last line of its block: an empty quoted
        // line before it would put it in a paragraph of its own
        let html = this.parse(markdown.replace(
            new RegExp(`(?:\\n>[ \\t]*)+\\n(>[ \\t]*\\[[^\\]]*\\]\\(${NOT_VERIFIED_ANCHOR}\\))`, 'g'), '\n$1'));

        // No href, so clicking it does nothing
        html = html.replace(
            new RegExp(`<a href="${NOT_VERIFIED_ANCHOR}">[^<]*</a>`, 'g'),
            `<a class="${NOT_VERIFIED_CLASS}">${NOT_VERIFIED_LABEL}</a>`);

        // Make sure links open in a new tab
        html = html.replace(/<a /g, '<a target="_blank"');

        // The answer is model output, and marked passes raw HTML through: strip anything
        // that could run script (handlers, script elements, javascript: links) before
        // the result is marked as safe to show
        return this.sanitizer.bypassSecurityTrustHtml(
            this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '');
    }

    /**
     * Copy an answer as formatted HTML (which Word, Outlook and Excel paste as headings, bold,
     * lists and tables) and as text, for editors that only take text. The sources are listed once
     * at the end instead of being cited inline.
     *
     * @returns whether anything was copied
     */
    async copy(markdown: string): Promise<boolean> {
        const { body, sources } = this.withoutCitations(markdown);
        const text = sources.length
            ? `${body}\n\nReferences\n${sources.map(source => `- ${source.label}: ${source.url}`).join('\n')}`
            : body;
        try {
            const formatted = sources.length
                ? `${body}\n\n**References**\n\n${sources.map(source => `- [${source.label}](${source.url})`).join('\n')}`
                : body;
            // Word pastes a table without borders unless the table asks for them
            const html = this.parse(formatted).replace(/<table>/g, '<table border="1" cellpadding="4" style="border-collapse: collapse">');
            await navigator.clipboard.write([new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([text], { type: 'text/plain' }),
            })]);
            return true;
        } catch (error) {
            // Formatted content is not supported or not allowed here: copy the text alone
            try {
                await navigator.clipboard.writeText(text);
                return true;
            } catch (textError) {
                console.error('Failed to copy to clipboard:', textError);
                return false;
            }
        }
    }

    /**
     * Take the inline citations, their link definitions and the "Not verified by GPS" link out of
     * an answer. Returns what is left and the sources, in the order they were first cited, with
     * absolute links so they work where they are pasted.
     */
    withoutCitations(markdown: string): { body: string, sources: AnswerSource[] } {
        const urls = new Map<string, string>();
        let body = markdown.replace(/^\[([^\]]+)\]:[ \t]*(\S+)[ \t]*\n?/gm, (_match, label: string, url: string) => {
            urls.set(label, url.startsWith('/') ? window.location.origin + url : url);
            return '';
        });
        body = body
            .replace(/^>?[ \t]*\[[^\]]*\]\(#not-verified-by-gps\)[ \t]*\n?/gm, '')
            .replace(/[ \t]*\[[^\]]*\]\(#not-verified-by-gps\)/g, '');

        const cited: string[] = [];
        body = body.replace(/[ \t]*\[([^\]]+)\](?![(:[])/g, (match, label: string) => {
            if (!urls.has(label)) {
                return match;
            }
            if (!cited.includes(label)) {
                cited.push(label);
            }
            return '';
        });
        const labels = [...cited, ...Array.from(urls.keys()).filter(label => !cited.includes(label))];
        return { body: body.trim(), sources: labels.map(label => ({ label, url: urls.get(label) as string })) };
    }

    private parse(markdown: string): string {
        return marked.parse(markdown, { async: false }) as string;
    }
}
